import React, { useState, useEffect } from 'react';
import { Table, Input, Button, message, Row, Col, Space } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SearchOutlined, PlusOutlined, EditOutlined, ZoomInOutlined } from '@ant-design/icons';
import { getAllReferrers } from '../../services/referrerService';
import Page from '../../components/layout/Page';
import ReferrerForm from '../../components/ReferrerModal/ReferrerForm';

const Referrers = () => {
    const { t } = useTranslation();
    const [referrers, setReferrers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedReferrer, setSelectedReferrer] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchReferrers();
    }, []);

    const fetchReferrers = async () => {
        try {
            setLoading(true);
            const data = await getAllReferrers();
            setReferrers(data);
        } catch (error) {
            console.error('Error fetching referrers:', error);
            message.error(t('common.error'));
        } finally {
            setLoading(false);
        }
    };

    const getColumnSearchProps = dataIndex => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm)}
                    style={{ width: 188, marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
        onFilter: (value, record) =>
            record[dataIndex]
                ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
                : '',
    });

    const handleSearch = (selectedKeys, confirm) => {
        confirm();
        setSearchText(selectedKeys[0]);
    };

    const handleReset = clearFilters => {
        clearFilters();
        setSearchText('');
    };

    const columns = [
        {
            title: t('common.actions'),
            key: 'actions',
            fixed: 'left',
            width: 150,
            render: (_, record) => (
                <Space size="middle">
                    <Button 
                        onClick={() => alert("Ansicht noch nicht umgesetzt")}
                        icon={<ZoomInOutlined />}
                        title="View Details"
                    >
                    </Button>                    
                    <Button 
                        onClick={() => handleEdit(record)}
                        icon={<EditOutlined />}
                        title="Edit Referred By"
                    >
                    </Button>
                </Space>
            ),
        },        
        {
            title: t('referrer.fields.firstName'),
            dataIndex: 'first_name',
            key: 'first_name',
            sorter: (a, b) => a.first_name?.localeCompare(b.first_name),
            ...getColumnSearchProps('first_name'),
        },
        {
            title: t('referrer.fields.name'),
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => a.name?.localeCompare(b.name),
            ...getColumnSearchProps('name'),
        },
        {
            title: t('referrer.fields.company'),
            dataIndex: 'company',
            key: 'company',
            sorter: (a, b) => a.company?.localeCompare(b.company),
            ...getColumnSearchProps('company'),
        },
        {
            title: t('referrer.fields.zipCode'),
            dataIndex: 'zip_code',
            key: 'zip_code',
            width: 100,
            sorter: (a, b) => a.zip_code?.localeCompare(b.zip_code),
            ...getColumnSearchProps('zip_code'),
        },
        {
            title: t('referrer.fields.city'),
            dataIndex: 'city',
            key: 'city',
            sorter: (a, b) => a.city?.localeCompare(b.city),
            ...getColumnSearchProps('city'),
        },
        {
            title: t('referrer.fields.email'),
            dataIndex: 'email',
            key: 'email',
            sorter: (a, b) => a.email?.localeCompare(b.email),
            ...getColumnSearchProps('email'),
        }
    ];

    const headerExtra = (
        <Row gutter={[8, 8]} align="middle">
            <Col xs={12} sm={12} md={12} lg={12}>
                <Input
                    placeholder={t('referrer.search')}
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    style={{ width: '100%' }}
                />
            </Col>
            <Col xs={12} sm={12} md={12} lg={12}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => handleAdd()}
                    style={{ width: '100%' }}
                >
                    {t('referrer.add')}
                </Button>
            </Col>
        </Row>
    );

    const filteredReferrers = referrers.filter(referrer =>
        referrer.name?.toLowerCase().includes(searchText.toLowerCase()) ||
        referrer.first_name?.toLowerCase().includes(searchText.toLowerCase()) ||
        referrer.company?.toLowerCase().includes(searchText.toLowerCase())
    );

    const handleEdit = (record) => {
        setSelectedReferrer(record);
        setIsModalVisible(true);
    };

    const handleAdd = () => {
        setSelectedReferrer(null);
        setIsModalVisible(true);
    };

    return (
        <Page
            title={t('referrer.title')}
            subtitle={t('referrer.subtitle')}
            headerExtra={headerExtra}
        >
            <div style={{ 
                width: '100%',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
            }}>
                <Table
                    columns={columns}
                    dataSource={filteredReferrers}
                    loading={loading}
                    rowKey="id"
                    scroll={{ x: 'max-content' }}
                />
            </div>

            <ReferrerForm
                visible={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    setSelectedReferrer(null);
                }}
                onSuccess={() => {
                    setIsModalVisible(false);
                    setSelectedReferrer(null);
                    fetchReferrers();
                }}
                initialValues={selectedReferrer}
            />
        </Page>
    );
};

export default Referrers;
